import type { AdminGroup, AdminProduct } from '../api/group/groupAdminApiSlice';
import type { ProductCategory } from '../api/productCategory/productCategoryApiSlice';

export type EventTypeInput = 'select' | 'freeText';

/**
 * How the admin venue booking screens ask for the event of a booking in THIS
 * room: the event-type dropdown (`select`) or a free-text name staff type,
 * e.g. a club's "Damen 2" (`freeText`).
 *
 * The admin-ui counterpart of the end-user app's `resolveEventTypeInput`
 * (`apps/end-user/src/util/categoryConfig.ts`). Same config key and same
 * reading — category only, no use-case fallback, anything but the exact
 * `freeText` reads as `select` — but a different source for the category: the
 * end-user step asks the category the customer ENTERED through, this asks the
 * selected room's own category. So a customer entering without a category gets
 * the dropdown on a free-text room, while staff get the text field. The setting
 * itself is written by `CategoryConfigForm`
 * (`docs/architecture/tenant-configurable-event-types.md`).
 *
 * The room is resolved as Group → its Product (`AdminGroup.productId`) → the
 * Product's category (`AdminProduct.productCategoryId`). Any missing link —
 * still loading, an archived room, a product filed under no category — reads
 * as `select`, today's dropdown: the dropdown still shows a stored free-text
 * name (`useEventTypeOptions` appends it), it only does not offer typing a new
 * one. A FAILED read is not a missing link — `useEventTypeInput` reports it
 * separately.
 */
export const resolveEventTypeInputForGroup = (
    groupId: string | null | undefined,
    groups: readonly Pick<AdminGroup, 'id' | 'productId'>[] | undefined,
    products: readonly Pick<AdminProduct, 'id' | 'productCategoryId'>[] | undefined,
    categories: readonly Pick<ProductCategory, 'id' | 'config'>[] | undefined,
): EventTypeInput => {
    if (!groupId) return 'select';
    const productId = groups?.find((g) => g.id === groupId)?.productId;
    if (!productId) return 'select';
    const categoryId = products?.find((p) => p.id === productId)?.productCategoryId;
    if (!categoryId) return 'select';
    const category = categories?.find((c) => c.id === categoryId);
    return category?.config?.eventTypeInput === 'freeText' ? 'freeText' : 'select';
};

/** Drops control characters (`\p{C}`) from a typed event name — the same
 *  rule the end-user app applies to its free-text field. */
export const withoutControlCharacters = (value: string): string => value.replace(/\p{C}/gu, '');
